import React from "react";
import PropTypes from "prop-types";

import { Button } from "semantic-ui-react";
import { formatDate } from "utils";

import { useFeatures } from "components/FeatureFlag";
import Modal from "components/Modal";
import { ModalWithTracker } from "components/Modal/Modal";
import { ModalHeaderWithTracker } from "components/Modal/ModalHeader";

import { MailToMessage } from "scenes/Authentication/components/Layout/components";

import { useCancelSubscriptionMutation } from "./hooks";

function CancelSubscriptionConfirmationModal({
  subscription,
  onCancelled,
  onClose,
}) {
  const { cancelSubscription, loading } =
    useCancelSubscriptionMutation(onCancelled);

  const refundEnabled =
    useFeatures("automated-refund") && subscription.refundable;

  const getFormattedDate = () => {
    const dateFormatter = formatDate("simpleDate");

    return <b>{dateFormatter(subscription?.nextBillingDate)}</b>;
  };

  return (
    <ModalWithTracker
      isModalOpen
      size="small"
      onClose={onClose}
      closeOnDimmerClick={!loading}
      data-test="cancel-subscription-confirmation-modal"
    >
      <ModalHeaderWithTracker onClose={onClose} disabled={loading}>
        Are you sure you want to cancel your subscription?
      </ModalHeaderWithTracker>
      <Modal.Body>
        <p>
          Before you proceed with the cancellation, we&apos;d love a chance to
          make things right. Instead of saying goodbye, how about reaching out
          to our{" "}
          <MailToMessage subject="I'm seeking help before cancelling my Hubly's account">
            support
          </MailToMessage>
          ? They&apos;re here to help and ensure you get the best out of your
          experience.
        </p>

        <p>
          Still want to cancel your subscription? Just a heads-up on what
          happens next{refundEnabled ? " if you decide to" : ""}:
        </p>

        {refundEnabled && (
          <React.Fragment>
            <h5>Cancel immediately and refund</h5>
            <ul>
              <li>
                You and all users in your account will be immediately logged out
                of Hubly and won&apos;t be able to access the platform again.
              </li>
              <li>
                A refund will be issued for the full value of the current term
                charges and you will be notified once the refund is processed.
              </li>
              <li>
                Your sensitive data will be deleted from Hubly after you cancel.
              </li>
            </ul>

            <h5>Don&apos;t renew your subscription</h5>
          </React.Fragment>
        )}

        <ul>
          <li>
            You won&apos;t be able to log in to Hubly after {getFormattedDate()}
            .
          </li>
          <li>
            Your sensitive data will be deleted from Hubly after{" "}
            {getFormattedDate()}.
          </li>
        </ul>

        <p>
          {refundEnabled ? "In both cases, t" : "T"}o reactivate your
          subscription, you&apos;ll need to contact{" "}
          <MailToMessage subject="I want to reactivate my Hubly's account">
            support
          </MailToMessage>
          .
        </p>
      </Modal.Body>
      <Modal.Footer>
        {refundEnabled && (
          <Button
            basic
            color="red"
            loading={loading}
            disabled={loading}
            onClick={() => {
              return cancelSubscription(subscription.id, true);
            }}
          >
            Cancel Immediately and Refund
          </Button>
        )}

        <Button
          basic
          color={refundEnabled ? "grey" : "red"}
          loading={loading}
          disabled={loading}
          onClick={() => {
            return cancelSubscription(subscription.id, false);
          }}
        >
          {subscription.status === "Free Trial"
            ? "Don't Activate Subscription"
            : "Don't Renew Subscription"}
        </Button>

        <Button primary disabled={loading} onClick={onClose}>
          Stay with Hubly
        </Button>
      </Modal.Footer>
    </ModalWithTracker>
  );
}

CancelSubscriptionConfirmationModal.propTypes = {
  subscription: PropTypes.shape({
    id: PropTypes.string.isRequired,
    nextBillingDate: PropTypes.instanceOf(Date),
    refundable: PropTypes.bool,
    status: PropTypes.oneOf([
      "Future",
      "Free Trial",
      "Active",
      "Non Renewing",
      "Paused",
      "Cancelled",
      "Pending Changes",
    ]).isRequired,
  }).isRequired,
  onCancelled: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

CancelSubscriptionConfirmationModal.defaultProps = {};

export default CancelSubscriptionConfirmationModal;
